/**
 * Lobby init query — derives from useLobbyDataQuery
 *
 * Previously this made a separate /lobby/init API call. Now the data is
 * included in the unified /game/lobby response, so this hook simply
 * extracts the relevant fields from useLobbyDataQuery.
 *
 * Consumers (useCategoriesQuery, useProvidersQuery, useLiveCategoriesQuery)
 * continue to work unchanged.
 */

import { useLobbyDataQuery, type LobbyCategory, type LobbyLiveCategory, type JackpotAmount, type JackpotConfig } from './useLobbyDataQuery';

export type { LobbyCategory, LobbyLiveCategory, JackpotAmount, JackpotConfig };

export interface LobbyInitData {
  success: boolean;
  categories: LobbyCategory[];
  liveCategories: LobbyLiveCategory[];
  providers: string[];
  jackpot: {
    amounts: JackpotAmount[];
    configs: JackpotConfig[];
  };
  liveGamesEnabled: boolean;
  cachedAt?: string;
}

/**
 * Unified lobby initialization query
 *
 * Derives from useLobbyDataQuery — no separate API call.
 *
 * IMPORTANT: Always called with useLobbyDataQuery defaults (section='casino',
 * gamesPerCategory=50) → queryKey ['lobby-data', 'casino', 50]. All downstream
 * hooks (useLobbyCategories, useLobbyProviders, etc.) share this single cache
 * entry. This works because allCategories is section-agnostic. Do NOT add a
 * section parameter here — it would split the cache and break downstream hooks
 * that expect a single shared query.
 */
export function useLobbyInitQuery() {
  const query = useLobbyDataQuery();

  return {
    ...query,
    data: query.data ? {
      success: true,
      categories: query.data.allCategories,
      liveCategories: query.data.liveCategories,
      providers: query.data.providers,
      jackpot: query.data.jackpot,
      liveGamesEnabled: query.data.liveGamesEnabled,
      cachedAt: query.data.cachedAt,
    } as LobbyInitData : undefined,
  };
}

/**
 * Hook to get categories from the lobby data with optional filtering
 */
export function useLobbyCategories(section?: string, excludePlaythrough?: boolean) {
  const { data } = useLobbyInitQuery();
  let categories = data?.categories || [];

  if (section) {
    categories = categories.filter(c => c.section === section);
  }
  if (excludePlaythrough) {
    categories = categories.filter(c => !c.reduced_redeemable);
  }

  return categories;
}

/**
 * Hook to get just the live categories from the lobby data
 */
export function useLobbyLiveCategories() {
  const { data } = useLobbyInitQuery();
  return data?.liveCategories || [];
}

/**
 * Hook to get just the providers from the lobby data
 */
export function useLobbyProviders() {
  const { data } = useLobbyInitQuery();
  return data?.providers || [];
}

/**
 * Hook to get jackpot data from the lobby data
 */
export function useLobbyJackpot() {
  const { data } = useLobbyInitQuery();
  return data?.jackpot || { amounts: [], configs: [] };
}

/**
 * Hook to check if live games are enabled
 */
export function useLiveGamesEnabled() {
  const { data } = useLobbyInitQuery();
  return data?.liveGamesEnabled ?? true;
}

/**
 * Hook to get lobby init data with loading/error states
 */
export function useLobbyInitData() {
  return useLobbyInitQuery();
}
